// Linked List — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Linked List Explanation modal. Same shape as binarySearchDrills.js: concise steps + an
// interactive "watch it happen" visualizer per version. Four drills cross-reference the
// curated problems instead of duplicating content, mirroring arraysDrills.js's Kadane's stub.

const linkedListDrills = [
  {
    id: 'traverse-and-count-length',
    title: 'Traverse a Linked List and Count Its Length',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Recursive Traversal',
        explanation: 'Count 1 for the current node, plus however long the rest of the list is (found by recursing on curr.next). Elegant, but each node adds a frame to Java\'s own call stack — a very long list risks a StackOverflowError.',
        steps: [
          'If the current node is null, the length of "nothing" is 0 — the base case.',
          'Otherwise, the length is 1 plus the length of everything after this node.',
          'Recurse on curr.next to get that remaining length.',
          'Every node adds one frame to the call stack, which is finite.',
        ],
        code:
`public int lengthBasic(ListNode head) {
    if (head == null) return 0;
    return 1 + lengthBasic(head.next);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — one call-stack frame per node' },
      },
      {
        name: 'Optimized — Iterative Traversal',
        explanation: 'Walk the list with a simple loop and a counter — no recursion, no call-stack growth, no matter how long the list is.',
        steps: [
          'Start a counter at 0 and curr at head.',
          'While curr isn\'t null, increment the counter and move curr = curr.next.',
          'Return the counter once curr falls off the end.',
          'Constant extra space, regardless of list length.',
        ],
        visual: {
          arrays: [{ label: 'list', values: [10, 20, 30, 40] }],
          varNames: ['count'],
          steps: [
            { highlights: { list: [0] }, vars: { count: 1 }, note: 'curr at node 0 — count = 1.' },
            { highlights: { list: [1] }, vars: { count: 2 }, note: 'curr at node 1 — count = 2.' },
            { highlights: { list: [2] }, vars: { count: 3 }, note: 'curr at node 2 — count = 3.' },
            { highlights: { list: [3] }, vars: { count: 4 }, note: 'curr at node 3 — count = 4. curr.next is null next — loop ends. Length: 4.' },
          ],
        },
        code:
`public int length(ListNode head) {
    int count = 0;
    ListNode curr = head;
    while (curr != null) {
        count++;
        curr = curr.next;
    }
    return count;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-middle-node',
    title: 'Find the Middle Node of a Linked List',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Two Passes',
        explanation: 'First walk the whole list once just to count its length. Then walk it again, this time stopping halfway. Correct, but the list is fully traversed twice.',
        steps: [
          'First pass: walk the entire list, counting its length n.',
          'Compute the middle position as n / 2.',
          'Second pass: walk from head again, stopping after n / 2 steps.',
          'Two full traversals — still O(n), but with a bigger constant than necessary.',
        ],
        code:
`public ListNode findMiddleBasic(ListNode head) {
    int n = 0;
    for (ListNode curr = head; curr != null; curr = curr.next) n++;

    ListNode curr = head;
    for (int i = 0; i < n / 2; i++) curr = curr.next;
    return curr;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Fast & Slow Pointers, One Pass',
        explanation: 'This is the fast/slow pointer technique from the Two Pointers pattern, applied where it matters most: a linked list has no length field to read upfront, so the array-based "just use length/2" shortcut isn\'t available here at all. slow advances one step per iteration, fast advances two — when fast runs out of list, slow is exactly at the middle.',
        steps: [
          'Start both slow and fast at head.',
          'While fast isn\'t null and fast.next isn\'t null, move slow one step and fast two steps.',
          'The moment fast can\'t move a full two steps further, stop.',
          'slow is now the middle node — found in a single pass.',
        ],
        visual: {
          arrays: [{ label: 'list', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { list: { primary: [0], secondary: [0] } }, note: 'slow=0, fast=0 — start.' },
            { highlights: { list: { primary: [1], secondary: [2] } }, note: 'slow moves to 1 (+1), fast moves to 2 (+2).' },
            { highlights: { list: { primary: [2], secondary: [4] } }, note: 'slow moves to 2, fast moves to 4 — the last node. fast.next is null — stop. slow=2 is the middle (value 3).' },
          ],
        },
        code:
`public ListNode findMiddle(ListNode head) {
    ListNode slow = head, fast = head;
    while (fast != null && fast.next != null) {
        slow = slow.next;
        fast = fast.next.next;
    }
    return slow;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'reverse-linked-list-drill',
    title: 'Reverse a Linked List',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Reverse Linked List\" there for the complete step-by-step and both code versions. You can also try it " +
      "hands-on in the \"Reverse the List\" mode of the Interactive Practice playground with any list size.",
  },
  {
    id: 'remove-duplicates-sorted-list',
    title: 'Remove Duplicates From a Sorted Linked List',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Track Seen Values in a HashSet',
        explanation: 'Walk the list, keeping a HashSet of values already kept. Skip (unlink) any node whose value has already been seen. Correct on ANY list, sorted or not — but pays for a HashSet even though the list being sorted makes that unnecessary.',
        steps: [
          'Keep a HashSet of values already added to the result.',
          'Walk the list once. For each node, check whether its value is already in the set.',
          'If it is, unlink this node (skip over it). If not, add its value to the set and keep the node.',
          'Uses extra space proportional to the number of distinct values.',
        ],
        code:
`public ListNode deleteDuplicatesBasic(ListNode head) {
    Set<Integer> seen = new HashSet<>();
    ListNode dummy = new ListNode(0, head);
    ListNode prev = dummy, curr = head;
    while (curr != null) {
        if (!seen.add(curr.val)) {
            prev.next = curr.next;   // skip the duplicate
        } else {
            prev = curr;
        }
        curr = curr.next;
    }
    return dummy.next;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the HashSet' },
      },
      {
        name: 'Optimized — Compare Adjacent Nodes Only',
        explanation: 'Because the list is SORTED, every duplicate is guaranteed to sit immediately next to its match — there\'s no need to remember anything from earlier in the list. Just compare each node to the one right after it.',
        steps: [
          'Start curr at head.',
          'While curr and curr.next both exist, compare their values.',
          'If they\'re equal, unlink curr.next (skip it) without advancing curr — there might be a third copy right after.',
          'If they differ, advance curr forward. No extra memory needed at all.',
        ],
        visual: {
          arrays: [{ label: 'list', values: [1, 1, 2, 3, 3] }],
          varNames: [],
          steps: [
            { highlights: { list: { primary: [0], secondary: [1] } }, note: 'curr=0 (1), curr.next=1 (1): equal — unlink node 1. curr stays at 0.' },
            { highlights: { list: { primary: [0], secondary: [2] } }, snapshots: { list: [1, 2, 3, 3] }, note: 'curr.next is now node "2" (value 2): 1 ≠ 2 — advance curr to this node.' },
            { highlights: { list: { primary: [1], secondary: [2] } }, note: 'curr=2, curr.next=3: 2 ≠ 3 — advance curr.' },
            { highlights: { list: { primary: [2], secondary: [3] } }, note: 'curr=3 (first), curr.next=3 (second): equal — unlink the second 3. Final list: [1, 2, 3].' },
          ],
        },
        code:
`public ListNode deleteDuplicates(ListNode head) {
    ListNode curr = head;
    while (curr != null && curr.next != null) {
        if (curr.val == curr.next.val) {
            curr.next = curr.next.next;   // unlink the duplicate
        } else {
            curr = curr.next;
        }
    }
    return head;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'detect-cycle-drill',
    title: 'Detect a Cycle in a Linked List',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Linked List Cycle\" there for the complete step-by-step and both code versions. You can also try it " +
      "hands-on in the \"Detect a Cycle\" mode of the Interactive Practice playground — build any list and " +
      "choose whether it cycles back.",
  },
  {
    id: 'merge-two-sorted-lists-drill',
    title: 'Merge Two Sorted Linked Lists',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Merge Two Sorted Lists\" there for the complete step-by-step and both code versions. You can also try " +
      "it hands-on in the \"Merge Two Sorted Lists\" mode of the Interactive Practice playground with any two " +
      "lists of your choosing.",
  },
  {
    id: 'reverse-linked-list-in-groups-of-k',
    title: 'Reverse a Linked List in Groups of K',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Reverse Each Group Using a Stack',
        explanation: 'For each group of k nodes, push their values onto a stack, then pop them back off (in reversed order) to rewrite the group. Correct, and easy to reason about — but pays for a stack per group even though the reversal could happen with pointer rewiring alone.',
        steps: [
          'Walk the list k nodes at a time.',
          'For each group, push all k node values onto a stack.',
          'Pop the stack to rewrite the group\'s values in reversed order.',
          'If fewer than k nodes remain at the end, leave that final partial group untouched.',
        ],
        code:
`// Simplified sketch — pushes/pops VALUES for clarity; a full version relinks actual nodes.
public ListNode reverseKGroupBasic(ListNode head, int k) {
    ListNode curr = head;
    while (curr != null) {
        Deque<Integer> stack = new ArrayDeque<>();
        ListNode temp = curr;
        int count = 0;
        while (temp != null && count < k) { stack.push(temp.val); temp = temp.next; count++; }

        if (count == k) {   // only rewrite a FULL group
            ListNode writer = curr;
            while (!stack.isEmpty()) { writer.val = stack.pop(); writer = writer.next; }
        }
        curr = temp;
    }
    return head;
}`,
        complexity: { time: 'O(n)', space: 'O(k) — the stack for one group at a time' },
      },
      {
        name: 'Optimized — In-Place Pointer Rewiring Per Group',
        explanation: 'Apply the same prev/curr/next rewiring from the classic list-reversal drill, but only within one group of k nodes at a time — then stitch that reversed group back into the surrounding list, using a dummy node and a "group tail" pointer to connect each reversed group to the next.',
        steps: [
          'Check whether at least k nodes remain; if not, leave the rest of the list exactly as-is.',
          'Reverse exactly k nodes using the standard prev/curr/next in-place technique.',
          'Connect the previous group\'s tail to this group\'s new head, and remember this group\'s original head as the new tail for the next connection.',
          'Repeat for the next group starting where this one left off — no extra data structure needed.',
        ],
        visual: {
          arrays: [{ label: 'list (k = 2)', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { list: { primary: [0], secondary: [1] } }, note: 'Group 1: nodes [1, 2] — a full group of k=2. Reverse in place.' },
            { snapshots: { list: [2, 1, 3, 4, 5] }, highlights: { list: { primary: [2], secondary: [3] } }, note: 'After reversing group 1: [2, 1, 3, 4, 5]. Group 2: nodes [3, 4] — another full group. Reverse in place.' },
            { snapshots: { list: [2, 1, 4, 3, 5] }, highlights: { list: [4] }, note: 'After reversing group 2: [2, 1, 4, 3, 5]. Only node [5] remains — fewer than k=2, so it\'s left untouched. Final: [2, 1, 4, 3, 5].' },
          ],
        },
        code:
`public ListNode reverseKGroup(ListNode head, int k) {
    ListNode dummy = new ListNode(0);
    dummy.next = head;
    ListNode groupPrev = dummy;   // the node right before the current group

    while (true) {
        ListNode kth = groupPrev;
        for (int i = 0; i < k && kth != null; i++) kth = kth.next;
        if (kth == null) break;        // fewer than k nodes remain — stop, leave the rest as-is

        ListNode groupNext = kth.next;  // node right after this group
        ListNode prev = groupNext, curr = groupPrev.next;
        while (curr != groupNext) {     // standard in-place reversal, bounded to this group
            ListNode next = curr.next;
            curr.next = prev;
            prev = curr;
            curr = next;
        }

        ListNode groupOldHead = groupPrev.next;   // becomes this group's new tail
        groupPrev.next = kth;                      // kth is this group's new head
        groupPrev = groupOldHead;                   // set up for the next group
    }
    return dummy.next;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'remove-nth-from-end-drill',
    title: 'Remove the Nth Node From the End of a List',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Remove Nth Node From End of List\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'intersection-of-two-linked-lists',
    title: 'Find Where Two Linked Lists Intersect',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — HashSet of Visited Nodes',
        explanation: 'Walk list A, remembering every node object visited in a HashSet. Then walk list B, checking each node against that set — the first one found in the set is where the lists merge.',
        steps: [
          'Walk list A completely, adding every node (by reference, not value) to a HashSet.',
          'Walk list B from its head.',
          'The first node in B that\'s already in the set is the intersection point.',
          'If B is exhausted with no match, the lists don\'t intersect.',
        ],
        code:
`public ListNode getIntersectionBasic(ListNode headA, ListNode headB) {
    Set<ListNode> visited = new HashSet<>();
    for (ListNode node = headA; node != null; node = node.next) {
        visited.add(node);
    }
    for (ListNode node = headB; node != null; node = node.next) {
        if (visited.contains(node)) return node;
    }
    return null;
}`,
        complexity: { time: 'O(m + n)', space: 'O(m) — one HashSet entry per node in list A' },
      },
      {
        name: 'Optimized — Two Pointers, Swap Lists at the End',
        explanation: 'Walk both lists at once with two pointers. When a pointer reaches the end of its own list, redirect it to the HEAD of the OTHER list instead of stopping. Both pointers then travel the exact same total distance (|A| + |B|) before potentially meeting — which perfectly cancels out the length difference between the two lists without ever computing it explicitly.',
        steps: [
          'Start pA at headA and pB at headB.',
          'Advance both one node at a time.',
          'The moment a pointer reaches null, redirect it to the OTHER list\'s head (this happens at most once per pointer).',
          'The two pointers become equal exactly at the intersection node — or both become null at the same time if there\'s no intersection.',
        ],
        code:
`public ListNode getIntersection(ListNode headA, ListNode headB) {
    ListNode pA = headA, pB = headB;
    while (pA != pB) {
        pA = (pA == null) ? headB : pA.next;
        pB = (pB == null) ? headA : pB.next;
    }
    return pA;   // the intersection node, or null if they never meet
}`,
        complexity: { time: 'O(m + n)', space: 'O(1)' },
      },
    ],
  },
];

export default linkedListDrills;
